import React, { useMemo } from "react";
import { usePost } from "../../../../../../api/useCall";
import { useQuantities } from "../../../../../../api/useQuantities";
import { useSetups, type Setup } from "../../../../../../api/useSetups";
import t from "../../../../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import ModalForm from "@ui/Modal/Form";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import DropzoneV2 from "@ui/DropzoneV2";
import type {
  ObservableParsingError,
  ObservableParsingResult,
} from "../../../../../../domain/parser/observable/csv";
import { parse as parseObservableFile } from "../../../../../../domain/parser/observable/csv";
import { getObservableParsingErrorMessage } from "../../../../../../domain/parser/observable/csv/errors";
import { isResultErr } from "../../../../../../lib/patterns/result";
import useActiveAnalysesStream from "../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../api/useProjects";
import { useImpactedAnalyses } from "../../useAnalysisImpacted";
import { getCorpusGraph } from "diagnostics-lib/runtime";
interface P {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}

const analysisStatus = new Set(["DIAGNOSIS", "DIAGNOSIS_ERROR"] as const);

const ImportModalForm = ({ project, setup, onRequestClose }: P) => {
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const setupId = setup.id;
  const observables = setup.diagnosisInputs;
  const quantities = useQuantities(project.id);
  const displayWarning = observables.length > 0;
  const [filename, setFilename] = React.useState("");
  const [observableParsingResult, setObservableParsingResult] =
    React.useState<ObservableParsingResult>();
  const postObservables = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/inputs",
  );
  const { addToast } = useToasts();
  const [isValid, setIsValid] = React.useState<boolean>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<ObservableParsingError[]>();

  const impactedAnalysisCount = useImpactedAnalyses({
    setupId,
    activeAnalyses,
    setups: useSetups(project.id),
    analysisStatus,
  }).length;

  const downloadErrorDetails = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_observables_import_errors.json`);
    a.click();
  }, [errorDetails]);

  const handleSubmit = () => {
    if (observableParsingResult) {
      postObservables({
        routeParams: { projectId: project.id, setupId },
        body: { fileName: filename, inputs: observableParsingResult.data },
      })
        .then(() => {
          addToast(
            "success",
            t("setup.diagnosis.observable.import_observables.toast.success", {
              count: observableParsingResult.data.length,
            }),
          );
        })
        .catch(err => {
          addToast("error", err);
        });
    }
    onRequestClose();
    return true;
  };

  const corpus = useMemo(
    () =>
      quantities.reduce<Record<string, string | null>>(
        (acc, { name, formula }) => ((acc[name] = formula), acc),
        {},
      ),
    [quantities],
  );
  const graph = useMemo(() => {
    let valid, graph;
    try {
      ({ graph, valid } = getCorpusGraph(corpus));
    } catch (e) {
      return null;
    }

    return valid ? (graph as { [token: string]: string[] | null }) : null;
  }, [corpus]);
  const expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  } = useMemo(() => ({ corpus, graph }), [corpus, graph]);

  const handleDrop = React.useCallback(
    async ([file]: File[]) => {
      if (!file) return;
      /**
       * Keep track of the file name to display inside the dropzone.
       */
      setFilename(file.name);

      /**
       * Parse file according to the expected format and content rules.
       */
      const result = await parseObservableFile(file, {
        expectedNameValues: quantities.map(({ name }) => name),
        quantityIdByName: quantities.reduce<Record<string, string>>(
          (acc, q) => ((acc[q.name] = q.id), acc),
          {},
        ),
        expressionEngineInput,
      });

      /**
       * Catch for parsing errors.
       * Display humain readable error message and allow for downloading the error details.
       */
      if (isResultErr(result) && result.value[0]) {
        setIsValid(false);
        setErrorText(getObservableParsingErrorMessage(result.value[0]));
        setErrorDetails(result.value);
        return;
      }

      /**
       * Parsing was successful.
       * Save the result in the state to be used in the submit handler.
       */
      setIsValid(true);
      setObservableParsingResult(result.value as ObservableParsingResult);
    },
    [quantities, expressionEngineInput],
  );

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={isValid && (observableParsingResult?.data?.length || 0) > 0 ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
    >
      <DropzoneV2
        onDrop={handleDrop}
        downloadErrors={downloadErrorDetails}
        accept=".csv"
        pathSampleFile="/metroscopia_example_observables.csv"
        validation={isValid}
        nameFile={filename}
        errorText={isValid ? undefined : errorText}
      />
      {observableParsingResult && isValid && displayWarning && impactedAnalysisCount > 0 && (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.paragraphs.model.modal.delete_model.warningText", {
            impactedAnalysisCount,
          })}
        />
      )}
    </ModalForm>
  );
};

export default ImportModalForm;
